import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

function renderInline(text: string, blockIndex: number): ReactNode[] {
  return text.split(/(\*\*.+?\*\*|\[[^\]]+\]\(https?:\/\/[^\s)]+\))/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${blockIndex}-${index}`}>{part.slice(2, -2)}</strong>;
    }
    const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
    if (link) {
      return (
        <a key={`${blockIndex}-${index}`} href={link[2]} target="_blank" rel="noopener noreferrer">
          {link[1]}
        </a>
      );
    }
    return part;
  });
}

function renderMarkdown(markdown: string): ReactNode[] {
  const normalized = markdown.replace(/\r\n?/g, '\n').trim();

  return normalized.split(/\n\s*\n/).map((block, blockIndex) => {
    const heading = block.match(/^(#{1,2})\s+(.+)$/);
    if (heading) {
      const content = renderInline(heading[2], blockIndex);
      return heading[1].length === 1
        ? <h1 key={blockIndex}>{content}</h1>
        : <h2 key={blockIndex}>{content}</h2>;
    }

    const lines = block.split('\n');
    if (lines.every((line) => line.startsWith('- '))) {
      return (
        <ul key={blockIndex}>
          {lines.map((line, lineIndex) => (
            <li key={`${blockIndex}-${lineIndex}`}>{renderInline(line.slice(2), blockIndex)}</li>
          ))}
        </ul>
      );
    }

    if (/^---+$/.test(block.trim())) return null;

    return <p key={blockIndex}>{renderInline(block.replace(/\n/g, ' '), blockIndex)}</p>;
  });
}

export default function PrivacyPolicyPage({
  title = 'Privacy Policy',
  source = '/privacy-policy.md',
}: { title?: string; source?: string }) {
  const [markdown, setMarkdown] = useState('');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    document.title = `Ennobridge Tech | ${title}`;
    fetch(source)
      .then((response) => {
        if (!response.ok) throw new Error('Policy could not be loaded.');
        return response.text();
      })
      .then(setMarkdown)
      .catch(() => setHasError(true));
  }, [source, title]);

  return (
    <div id="privacy-policy">
      <style>{`
        #privacy-policy {
          min-height: 100vh;
          background: #ffffff;
          color: #263238;
          font-family: Arial, Helvetica, sans-serif;
        }
        #privacy-policy .brand-bar {
          border-bottom: 1px solid #e5eaf0;
        }
        #privacy-policy .brand-bar a {
          display: block;
          width: min(100% - 40px, 800px);
          margin: 0 auto;
          padding: 20px 0;
        }
        #privacy-policy .brand-bar img {
          width: auto;
          max-width: 100%;
        }
        #privacy-policy .brand-logo-compact {
          display: block;
          height: 40px;
        }
        #privacy-policy .brand-logo-full {
          display: none;
          height: 28px;
        }
        #privacy-policy .policy-content {
          width: min(100% - 40px, 800px);
          margin: 0 auto;
          padding: 40px 0 72px;
          line-height: 1.7;
        }
        #privacy-policy h1,
        #privacy-policy h2 {
          color: #17375e !important;
          font-family: Arial, Helvetica, sans-serif !important;
          line-height: 1.3 !important;
        }
        #privacy-policy h1 {
          margin: 0 0 8px !important;
          font-size: 32px !important;
        }
        #privacy-policy h2 {
          margin: 32px 0 12px !important;
          font-size: 22px !important;
        }
        #privacy-policy p,
        #privacy-policy ul {
          margin: 0 0 16px !important;
          font-family: Arial, Helvetica, sans-serif !important;
          font-size: 16px !important;
          line-height: 1.7 !important;
        }
        #privacy-policy ul {
          padding-left: 24px;
        }
        #privacy-policy li + li {
          margin-top: 8px;
        }
        #privacy-policy strong {
          color: #172b4d;
        }
        #privacy-policy .policy-content a {
          color: #17375e;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        @media (max-width: 600px) {
          #privacy-policy .brand-bar a,
          #privacy-policy .policy-content {
            width: min(100% - 32px, 800px);
          }
          #privacy-policy .policy-content {
            padding-top: 32px;
          }
          #privacy-policy h1 {
            font-size: 28px !important;
          }
        }
        @media (min-width: 640px) and (max-width: 1023px) {
          #privacy-policy .brand-logo-compact {
            height: 56px;
          }
        }
        @media (min-width: 1024px) {
          #privacy-policy .brand-logo-compact {
            display: none;
          }
          #privacy-policy .brand-logo-full {
            display: block;
          }
        }
      `}</style>
      <header className="brand-bar">
        <Link to="/" aria-label="Ennobridge Tech home">
          <img className="brand-logo-compact" src="/images/EB%20Logo.png" alt="Ennobridge Tech" />
          <img className="brand-logo-full" src="/images/Ennobridge%20LOGO%203.png" alt="Ennobridge Tech" />
        </Link>
      </header>
      <main className="policy-content" aria-busy={!markdown && !hasError}>
        {hasError
          ? <p>The {title.toLowerCase()} could not be loaded. Please try again later.</p>
          : markdown
            ? renderMarkdown(markdown)
            : <p>Loading {title.toLowerCase()}...</p>}
      </main>
    </div>
  );
}